import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";

export default function FeaturedProject({ project }) {
  const termRef = useRef(null);

  useEffect(() => {
    if (!termRef.current) return;
    const lines = termRef.current.querySelectorAll(".term-line");
    gsap.fromTo(
      lines,
      { opacity: 0, x: -10 },
      {
        opacity: 1,
        x: 0,
        stagger: 0.18,
        duration: 0.4,
        ease: "power2.out",
        scrollTrigger: {
          trigger: termRef.current,
          start: "top 80%",
        },
        delay: 0.3,
      }
    );
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      style={{
        gridColumn: "1 / -1",
        background: "var(--card)",
        display: "grid",
        gridTemplateColumns: "1fr 1.2fr",
        position: "relative",
        overflow: "hidden",
        border: "1px solid var(--border)",
      }}
      className="featured-inner"
    >
      {/* Left */}
      <div
        style={{
          padding: "3rem",
          borderRight: "1px solid var(--border)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.65rem",
            color: "var(--accent)",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            marginBottom: "1.5rem",
            border: "1px solid rgba(57,255,20,0.2)",
            display: "inline-block",
            padding: "0.25rem 0.7rem",
          }}
        >
          Featured — 01
        </div>

        <h3
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(2rem, 4vw, 3.2rem)",
            lineHeight: 1,
            marginBottom: "1rem",
          }}
        >
          {project.title}
        </h3>

        <p
          style={{
            fontSize: "0.92rem",
            color: "#666",
            lineHeight: 1.8,
            marginBottom: "1.8rem",
          }}
        >
          {project.desc}
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.4rem",
            marginBottom: "2rem",
          }}
        >
          {project.stack.map((s) => (
            <span
              key={s}
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.65rem",
                color: "var(--accent2)",
                border: "1px solid rgba(0,212,255,0.2)",
                padding: "0.25rem 0.6rem",
              }}
            >
              {s}
            </span>
          ))}
        </div>

        <a href={project.github} target="_blank" rel="noreferrer">
          <motion.button
            whileHover={{ backgroundColor: "var(--accent)", color: "#000" }}
            whileTap={{ scale: 0.97 }}
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.7rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              padding: "0.8rem 1.8rem",
              border: "1px solid var(--accent)",
              background: "transparent",
              color: "var(--accent)",
              cursor: "none",
              transition: "all 0.2s",
            }}
          >
            View on GitHub →
          </motion.button>
        </a>
      </div>

      {/* Right: Terminal */}
      <div
        style={{
          padding: "3rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `repeating-linear-gradient(
            45deg,
            #111 0, #111 1px,
            transparent 0, transparent 50%
          )`,
          backgroundSize: "20px 20px",
        }}
      >
        <div
          ref={termRef}
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.72rem",
            color: "#444",
            border: "1px solid var(--border)",
            padding: "1.5rem",
            width: "100%",
            maxWidth: 340,
            background: "#0a0a0a",
          }}
        >
          {/* Terminal header */}
          <div
            style={{
              display: "flex",
              gap: "0.4rem",
              marginBottom: "1rem",
              paddingBottom: "0.8rem",
              borderBottom: "1px solid var(--border)",
            }}
          >
            {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
              <div
                key={c}
                style={{ width: 10, height: 10, borderRadius: "50%", background: c }}
              />
            ))}
            <span style={{ marginLeft: "0.5rem", color: "#333", fontSize: "0.65rem" }}>
              resume_analyzer.sh
            </span>
          </div>

          {[
            { text: "$ analyze --resume ./yuvan.pdf", color: "#39ff14" },
            { text: "Parsing document...", color: "#444" },
            { text: "✓ Skills extracted: 24", color: "#00d4ff" },
            { text: "✓ JD match score: 87%", color: "#00d4ff" },
            { text: "  Gaps identified:", color: "#555" },
            { text: "  › Kubernetes (High)", color: "#888" },
            { text: "  › GraphQL (Medium)", color: "#888" },
            { text: "Generating questions...", color: "#444" },
            { text: "✓ 12 questions ready", color: "#39ff14" },
            { text: "✓ ATS resume generated ▌", color: "#39ff14", blink: true },
          ].map((line, i) => (
            <div
              key={i}
              className="term-line"
              style={{
                color: line.color,
                marginBottom: "0.35rem",
                opacity: 0,
              }}
            >
              {line.text}
              {line.blink && (
                <span
                  style={{
                    display: "inline-block",
                    background: "#39ff14",
                    width: 7,
                    height: "0.9em",
                    verticalAlign: "middle",
                    animation: "blink 1s step-end infinite",
                    marginLeft: 2,
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes blink { 50% { opacity: 0; } }
        @media (max-width: 900px) {
          .featured-inner { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </motion.div>
  );
}
